import {
  Avatar,
  Box,
  CircularProgress,
  FormControlLabel,
  InputAdornment,
  Pagination,
  Paper,
  Radio,
  Stack,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme
} from '@mui/material';
import { useFormikContext } from 'formik';
import { useEffect, useState } from 'react';
import { useDebounce } from 'src/hooks/useDebounce';
import { IClipping } from 'src/models/onlineMedia';
import SearchIcon from '@mui/icons-material/Search';
import useOnlineMedia from 'src/services/useOnlineMedia';
import useOfflineMedia from 'src/services/useOfflineMedia';
import { useAppSelector } from 'src/app/hooks';
import { TMediaType } from 'src/models/offlineMedia';
import { IFormValues } from './ModalCreateCustomDashboard';

const TableCellHeader = styled(TableCell)(
  ({ theme }) => `
        z-index: 3;
        background: ${theme.palette.primary.main};
        color: ${theme.palette.common.white};
    `
);

interface SelectClippingSectionProps {
  mediaType: TMediaType;
}

const SelectClippingSection = ({ mediaType }: SelectClippingSectionProps) => {
  const theme = useTheme();
  const [debouncedState, setDebouncedState] = useDebounce('');
  const [page, setPage] = useState(1);

  const { setFieldValue, values } = useFormikContext<IFormValues>();

  const { getClippingsV5 } = useOnlineMedia();
  const { getClippingOfflineMedia, resGetClippingOfflineMedia } =
    useOfflineMedia();
  const projectId = useAppSelector((state) => state.users.project.id);

  const selectedClipping =
    mediaType === 'onlineMedia' ? values.onmClipping : values.ofmClipping;
  const fieldName = mediaType === 'onlineMedia' ? 'onmClipping' : 'ofmClipping';

  const onmClippingData = useAppSelector(
    (state) => state.storeOnlineMediaStream.clippingList.data
  );

  const clippings =
    mediaType === 'onlineMedia'
      ? onmClippingData.result
      : resGetClippingOfflineMedia.data?.article_getClippingArticle?.result ??
        [];
  const limit = 10;
  const totalPages =
    mediaType === 'onlineMedia'
      ? onmClippingData.totalPages
      : resGetClippingOfflineMedia.data?.article_getClippingArticle?.totalPages;

  const totalRows =
    mediaType === 'onlineMedia'
      ? onmClippingData.totalRows
      : resGetClippingOfflineMedia.data?.article_getClippingArticle?.totalRows;

  const loadingOnm = useAppSelector(
    (state) => state.storeOnlineMediaStream.clippingList.loading
  );
  const loadingOfm = resGetClippingOfflineMedia.loading;

  const loading = mediaType === 'onlineMedia' ? loadingOnm : loadingOfm;

  const isSelected = (id: string) => selectedClipping?.id === id;

  // const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
  //   if (event.target.checked) {
  //     setFieldValue(fieldName, [...selectedClipping, ...clippings]);
  //     return;
  //   }
  //   setFieldValue(fieldName, []);
  // };

  const handleClickClipping = function (clipping: IClipping) {
    setFieldValue(fieldName, clipping);
  };

  const handleClickPage = (page: number) => {
    setPage(page);
    if (mediaType === 'onlineMedia')
      getClippingsV5({ projectId, page, limit, name: debouncedState ?? '' });

    if (mediaType === 'offlineMedia')
      getClippingOfflineMedia({
        variables: {
          projectId: projectId,
          categoryArticle: 'offlineMedia',
          page: page,
          limit: limit,
          name: debouncedState ?? ''
        }
      });
  };

  // reset page to 1 when debounced state changed
  useEffect(() => {
    if (mediaType === 'onlineMedia') {
      getClippingsV5({
        projectId: projectId,
        limit: 10,
        page: 1,
        name: debouncedState
      });
    }

    if (mediaType === 'offlineMedia') {
      getClippingOfflineMedia({
        variables: {
          projectId: projectId,
          categoryArticle: 'offlineMedia',
          page: 1,
          limit: 10,
          name: debouncedState
        }
      });
    }
    setPage(1);
  }, [debouncedState]);

  return (
    <Stack>
      <TextField
        fullWidth
        id="standard-basic"
        placeholder="Search label"
        variant="standard"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          )
        }}
        sx={{
          mb: theme.spacing(3)
        }}
        onChange={(e) => setDebouncedState(e.target.value)}
      />
      <Stack direction="column" gap={2}>
        <TableContainer component={Paper} sx={{ height: '300px' }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCellHeader align="left" size="small" colSpan={2}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontSize: 13, color: theme.palette.common.white }}
                  >
                    Clipping list
                  </Typography>
                </TableCellHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow hover={false}>
                  <TableCell align="center" colSpan={2}>
                    <Box
                      width="100%"
                      height="100%"
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                    >
                      <CircularProgress />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : clippings?.length <= 0 ? (
                <TableRow hover={false}>
                  <TableCell align="center" colSpan={2}>
                    <Box
                      sx={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center'
                      }}
                    >
                      <Avatar>N/A</Avatar>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                clippings.map((clipping) => {
                  const isChecked = isSelected(clipping.id);
                  return (
                    <TableRow key={clipping.id}>
                      <TableCell align="left" size="small" sx={{ width: '1%' }}>
                        <FormControlLabel
                          sx={{ marginLeft: 0 }}
                          control={
                            <Radio
                              size="small"
                              checked={isChecked}
                              onChange={(e) => {
                                handleClickClipping(clipping);
                              }}
                              name="labels"
                              color="primary"
                              value={clipping.id}
                            />
                          }
                          label=""
                        />
                      </TableCell>
                      <TableCell align="left" size="small">
                        {clipping.name}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Box>
          <Pagination
            count={totalPages}
            page={page}
            shape="rounded"
            color="primary"
            onChange={(_, page) => handleClickPage(page)}
            sx={{ '& .MuiPagination-ul': { justifyContent: 'right' } }}
          />
        </Box>
      </Stack>
    </Stack>
  );
};

export default SelectClippingSection;
